import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { mitErsatz } from "@/office/makro/pruefung";
import { istDeutsch, istEnglisch, readSprachen } from "@/office/word/pruefen";
import { MAKRO_DOKUMENT, vbaSprache } from "../makros";
import { readSeite } from "@/office/word/seite";

export const lektionSprache: Lesson = {
  id: "sprache",
  title: "Sprache und Silbentrennung",
  summary: "Warum Word plötzlich englische Rechtschreibung prüft.",
  durationMinutes: 7,
  learningGoals: [
    "Die Sprache eines Textabschnitts festlegen",
    "Verstehen, warum die Sprache eine Zeicheneigenschaft ist",
    "Die Silbentrennung aktivieren",
  ],
  steps: [
    {
      id: "sprache-festlegen",
      title: "Die Sprache festlegen",
      explanation: [
        "Ein häufiges Ärgernis: Ein aus einer anderen Quelle eingefügter Absatz wird " +
          "durchgehend rot unterringelt, obwohl alles richtig geschrieben ist. Der Grund " +
          "ist fast immer die Sprache – Word prüft den Abschnitt gegen ein englisches " +
          "Wörterbuch.",
        "Die Sprache ist nämlich keine Einstellung des Dokuments, sondern eine " +
          "Eigenschaft des Textes selbst, ähnlich wie Schriftart oder Fettung. Beim " +
          "Kopieren wandert sie mit.",
        "Zum Ändern markierst du den Text und wählst „Überprüfen“ → „Sprache“ → " +
          "„Sprache für die Korrekturhilfen festlegen“. Für ein importiertes Dokument " +
          "lohnt es sich, mit Strg + A alles zu markieren und die Sprache in einem Zug zu " +
          "setzen.",
        "Im selben Dialog steht „Rechtschreibung und Grammatik nicht prüfen“ – sinnvoll " +
          "für Codebeispiele oder Tabellen mit Artikelnummern, die sonst durchgehend " +
          "markiert würden.",
      ],
      keyPoints: [
        "Überprüfen → Sprache → Sprache für die Korrekturhilfen festlegen.",
        "Die Sprache klebt am Text und wird beim Kopieren mitgenommen.",
        "Strg + A und einmal setzen bereinigt ein importiertes Dokument.",
      ],
      task: {
        instruction:
          "Schreibe einen Absatz auf Englisch und weise ihm die Sprache Englisch zu. Alle " +
          "übrigen Absätze sollen auf Deutsch stehen.",
        hint: "Den englischen Absatz markieren, Überprüfen → Sprache → Sprache für die Korrekturhilfen festlegen → Englisch.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => {
              const sprachen = await readSprachen();
              return sprachen ? sprachen.map((absatz) => absatz.sprache) : null;
            },
            MAKRO_DOKUMENT,
            (bericht) =>
              [...bericht.gruppe("sprache").values()].flatMap((eintrag) =>
                Array<string | null>(Number(eintrag.get("absaetze") ?? 0)).fill(vbaSprache(Number(eintrag.get("id")))),
              ),
            (sprachen) => {
              const englisch = sprachen.filter((sprache) => istEnglisch(sprache)).length;
              const fremd = sprachen.filter((sprache) => !istEnglisch(sprache) && !istDeutsch(sprache)).length;
              return fromCriteria(
                [
                  criterion(`Mindestens ein Absatz ist als Englisch gekennzeichnet (gefunden: ${englisch})`, englisch > 0),
                  criterion(`Alle anderen Absätze stehen auf Deutsch (abweichend: ${fremd})`, fremd === 0),
                ],
                {
                  success: "Jeder Absatz wird jetzt gegen das richtige Wörterbuch geprüft.",
                  failure: "Die Sprachen stimmen noch nicht.",
                  hint: "Gemischte Absätze zählen als abweichend – Absatz vollständig markieren, dann die Sprache setzen.",
                },
              );
            },
          ),
      },
    },
    {
      id: "silbentrennung",
      title: "Silbentrennung",
      explanation: [
        "Im Absatz-Modul kam die Silbentrennung schon einmal vor: Ohne sie reißt " +
          "Blocksatz große Löcher zwischen die Wörter. Sie hängt direkt an der " +
          "eingestellten Sprache – Word trennt nach den Regeln der Sprache, die dem Text " +
          "zugewiesen ist.",
        "Aktiviert wird sie unter „Layout“ → „Silbentrennung“ → „Automatisch“. Das wirkt " +
          "auf das gesamte Dokument.",
        "Braucht ein einzelnes langes Wort eine Trennung an einer bestimmten Stelle, " +
          "setzt du mit Strg + Bindestrich einen bedingten Trennstrich. Er erscheint nur, " +
          "wenn das Wort tatsächlich am Zeilenende umbrochen wird – anders als ein " +
          "getippter Bindestrich, der auch mitten in der Zeile stehen bleibt, wenn sich " +
          "der Text verschiebt.",
      ],
      keyPoints: [
        "Layout → Silbentrennung → Automatisch.",
        "Die Trennregeln folgen der eingestellten Textsprache.",
        "Strg + Bindestrich setzt einen bedingten Trennstrich.",
        "Ein getippter Bindestrich bleibt stehen und wird zum Fehler.",
      ],
      task: {
        instruction: "Schalte für dein Dokument die automatische Silbentrennung ein.",
        hint: "Layout → Silbentrennung → Automatisch.",
        macro: MAKRO_DOKUMENT,
        macroNurErsatz: true,
        check: () =>
          mitErsatz(
            async () => (await readSeite())?.silbentrennung ?? null,
            MAKRO_DOKUMENT,
            (bericht) => bericht.janein("silbentrennung") === true,
            (an) =>
              fromCriteria([criterion("Die automatische Silbentrennung ist eingeschaltet", an)], {
                success: "Word trennt jetzt selbst – nach den Regeln der Textsprache.",
                failure: "Die automatische Silbentrennung ist noch aus.",
              }),
          ),
      },
    },
  ],
};
